import { FileAttachment } from 'api/attachments';
import { SimpleSearchFilter as SSF } from 'api/dashboard/widget';
import { ObjectType } from 'api/pushes';
import {
  FieldDataType,
  FieldTypes,
} from 'components/organisms/module-settings/fields/types';

export interface EntityMeta {
  conditions?: SSF;
}

export enum CustomerPortalWidgetType {
  HeroSection = 'HERO_SECTION',
  ProjectPlan = 'PROJECT_PLAN',
  SPACE = 'SPACE',
  AllFiles = 'ALL_FILES',
  FORMS = 'FORMS',
  ProjectUpdates = 'PROJECT_UPDATES',
  AccountFiles = 'ACCOUNT_FILES',
  AccountSpaces = 'ACCOUNT_SPACES',
  CHATS = 'CHATS',
  FAQ = 'FAQ',
  Metrics = 'METRICS',
  Feature = 'FEATURE',
  ImageGallery = 'IMAGE_GALLERY',
  Resources = 'RESOURCES',
  ProjectSummaryInformation = 'PROJECT_SUMMARY_INFORMATION',
  AccountSummaryInformation = 'ACCOUNT_SUMMARY_INFORMATION',
  AccountProjectsList = 'ACCOUNT_PROJECTS_LIST',
  ProjectForms = 'PROJECT_FORMS',
  ProjectHours = 'PROJECT_HOURS',
  ProjectOverview = 'PROJECT_OVERVIEW',
  TeamMembers = 'TEAM_MEMBERS',
  VideoList = 'VIDEO_LIST',
  CTA = 'CTA',
  Footer = 'FOOTER',
}

export enum CustomerPortalPageType {
  Home = 'HOME',
  Chat = 'CHAT',
  AccountOverview = 'ACCOUNT_OVERVIEW',
  Custom = 'CUSTOM',
}

export interface CustomerPortalLight {
  customerPortalId: number;
  customerPortalName: string;
  createdBy?: number;
  createdAt?: number;
  updatedBy?: number;
  updatedAt?: number;
}

export interface PortalMeta {
  pagesOrder: string[];
}

export interface CustomerPortal extends CustomerPortalLight {
  pages: CustomerPortalPage[];
  portalMeta: PortalMeta;
  customerPortalThemeId: number;
}

export interface CustomerPortalPageLight {
  pageName: string;
  customerPortalPageId: number;
  routePath: string;
  parentPortalId: number;
  pageReferenceId: string;
  createdBy?: number;
  createdAt?: number;
  deleted?: boolean;
  deleteable?: boolean;
  pageType: CustomerPortalPageType;
}

export interface PageMeta extends EntityMeta {
  sectionsOrder: string[];
}
export type SectionMeta = EntityMeta;
export type BlockMeta = EntityMeta;
export type WidgetMeta = EntityMeta & {
  input: Record<string, unknown>;
  styles: Record<string, unknown>;
};

export interface CustomerPortalPage extends CustomerPortalPageLight {
  pageMeta: PageMeta;
  sections: CustomerPortalSection[];
}

export interface CustomerPortalSectionLight {
  customerPortalSectionId: number;
  sectionName: string;
  parentPortalId: number;
  parentPageId: number;
  sectionReferenceId: string;
  deleted?: boolean;
}

export interface CustomerPortalSection extends CustomerPortalSectionLight {
  sectionMeta: SectionMeta;
  blocks: CustomerPortalBlock[];
}

export interface CustomerPortalBlock {
  blockId: string;
  blockName?: string;
  blockDefinition: BlockMeta;
  widget: CustomerPortalWidget;
}

export enum CpWidgetVariantType {
  Default = 'DEFAULT',
  HeroSectionFullBackground = 'HERO_SECTION_FULL_BACKGROUND',
  HeroSectionWithImageAtBottom = 'HERO_SECTION_WITH_IMAGE_AT_BOTTOM',
  HeroSectionWithImageAtCenter = 'HERO_SECTION_WITH_IMAGE_AT_CENTER',
  // FAQ
  FAQAccordion = 'FAQ_ACCORDION',
  FAQGrid = 'FAQ_GRID',
  FAQRow = 'FAQ_ROW',
  // Image Gallery
  ImageGalleryGrid = 'IMAGE_GALLERY_GRID',
  ImageGalleryCarousel = 'IMAGE_GALLERY_CAROUSEL',
  ImageGalleryRowCarousel = 'IMAGE_GALLERY_ROW_CAROUSEL',
  // Metrics
  MetricsGrid = 'METRICS_GRID',
  MetricsCardGrid = 'METRICS_CARD_GRID',
  MetricsRightSidedGrid = 'METRICS_RIGHT_SIDED_GRID',
  // Video List
  SingleVideo = 'SINGLE_VIDEO',
  VideoListGrid = 'VIDEO_LIST_GRID',
  VerticalVideoStack = 'VERTICAL_VIDEO_STACK',
  ScrollableSideMenu = 'SCROLLABLE_SIDE_MENU',
  // Resources
  ResourcesGrid = 'RESOURCES_GRID',
  ResourcesScrollableGrid = 'RESOURCES_SCROLLABLE_GRID',
  ResourcesList = 'RESOURCES_LIST',
  // CTA
  CTALeftAligned = 'CTA_LEFT_ALIGNED',
  CTACenterAligned = 'CTA_CENTER_ALIGNED',
  CTAStretched = 'CTA_STRETCHED',
  CTAStretchedWithBackground = 'CTA_STRETCHED_WITH_BACKGROUND',
  CTATitleAndContentSection = 'CTA_TITLE_AND_CONTENT_SECTION',
  // Feature
  FeatureWithImageOnLeft = 'FEATURE_WITH_IMAGE_ON_LEFT',
  FeatureWithImageOnRight = 'FEATURE_WITH_IMAGE_ON_RIGHT',
  // Footer
  FooterWithoutLinkGroups = 'FOOTER_WITHOUT_LINK_GROUPS',
  FooterWithLinkGroupOnright = 'FOOTER_WITH_LINK_GROUP_ON_RIGHT',
  FooterWithLinkGroupOnBottom = 'FOOTER_WITH_LINK_GROUP_ON_BOTTOM',

  // Project Forms
  SingleFormWithImageAtTheLeft = 'SINGLE_FORM_WITH_IMAGE_AT_THE_LEFT',
  SingleFormWithImageAtTheRight = 'SINGLE_FORM_WITH_IMAGE_AT_THE_RIGHT',
  SingleFormWithImageAtTheCenter = 'SINGLE_FORM_WITH_IMAGE_AT_THE_CENTER',
  MultiFormWithImageAtTheLeft = 'MULTI_FORM_WITH_IMAGE_AT_THE_LEFT',
  MultiFormWithAllRoundBorder = 'MULTI_FORM_WITH_ALL_ROUND_BORDER',
  MultiFormWithBottomBorder = 'MULTI_FORM_WITH_BOTTOM_BORDER',
}

const widgetsWithVariants = [
  CustomerPortalWidgetType.HeroSection,
  CustomerPortalWidgetType.FAQ,
  CustomerPortalWidgetType.ImageGallery,
  CustomerPortalWidgetType.Metrics,
  CustomerPortalWidgetType.VideoList,
  CustomerPortalWidgetType.Resources,
  CustomerPortalWidgetType.CTA,
  CustomerPortalWidgetType.Feature,
  CustomerPortalWidgetType.Footer,
  CustomerPortalWidgetType.ProjectForms,
];

export const doesWidgetHaveVariants = (widgetType: CustomerPortalWidgetType) =>
  widgetsWithVariants.includes(widgetType);

export const WidgetToVariantMap: Record<
  CustomerPortalWidgetType,
  CpWidgetVariantType[] | undefined
> = {
  [CustomerPortalWidgetType.HeroSection]: [
    CpWidgetVariantType.HeroSectionFullBackground,
    CpWidgetVariantType.HeroSectionWithImageAtBottom,
    CpWidgetVariantType.HeroSectionWithImageAtCenter,
  ],
  [CustomerPortalWidgetType.FAQ]: [
    CpWidgetVariantType.FAQAccordion,
    CpWidgetVariantType.FAQGrid,
    CpWidgetVariantType.FAQRow,
  ],
  [CustomerPortalWidgetType.ImageGallery]: [
    CpWidgetVariantType.ImageGalleryGrid,
    CpWidgetVariantType.ImageGalleryRowCarousel,
    CpWidgetVariantType.ImageGalleryCarousel,
  ],
  [CustomerPortalWidgetType.Metrics]: [
    CpWidgetVariantType.MetricsGrid,
    CpWidgetVariantType.MetricsCardGrid,
    CpWidgetVariantType.MetricsRightSidedGrid,
  ],
  [CustomerPortalWidgetType.VideoList]: [
    CpWidgetVariantType.SingleVideo,
    CpWidgetVariantType.VideoListGrid,
    CpWidgetVariantType.VerticalVideoStack,
    CpWidgetVariantType.ScrollableSideMenu,
  ],
  [CustomerPortalWidgetType.Resources]: [
    CpWidgetVariantType.ResourcesGrid,
    CpWidgetVariantType.ResourcesScrollableGrid,
    CpWidgetVariantType.ResourcesList,
  ],
  [CustomerPortalWidgetType.CTA]: [
    CpWidgetVariantType.CTALeftAligned,
    CpWidgetVariantType.CTACenterAligned,
    CpWidgetVariantType.CTAStretched,
    CpWidgetVariantType.CTAStretchedWithBackground,
    CpWidgetVariantType.CTATitleAndContentSection,
  ],
  [CustomerPortalWidgetType.Feature]: [
    CpWidgetVariantType.FeatureWithImageOnLeft,
    CpWidgetVariantType.FeatureWithImageOnRight,
  ],
  [CustomerPortalWidgetType.Footer]: [
    CpWidgetVariantType.FooterWithoutLinkGroups,
    CpWidgetVariantType.FooterWithLinkGroupOnright,
    CpWidgetVariantType.FooterWithLinkGroupOnBottom,
  ],
  [CustomerPortalWidgetType.ProjectPlan]: undefined,
  [CustomerPortalWidgetType.CHATS]: undefined,
  [CustomerPortalWidgetType.SPACE]: undefined,
  [CustomerPortalWidgetType.TeamMembers]: undefined,
  [CustomerPortalWidgetType.FORMS]: undefined,
  [CustomerPortalWidgetType.ProjectSummaryInformation]: undefined,
  [CustomerPortalWidgetType.AccountSummaryInformation]: undefined,
  [CustomerPortalWidgetType.AllFiles]: undefined,
  [CustomerPortalWidgetType.AccountFiles]: undefined,
  [CustomerPortalWidgetType.ProjectUpdates]: undefined,
  [CustomerPortalWidgetType.AccountSpaces]: undefined,
  [CustomerPortalWidgetType.AccountProjectsList]: undefined,
  [CustomerPortalWidgetType.ProjectHours]: undefined,
  [CustomerPortalWidgetType.ProjectOverview]: undefined,

  [CustomerPortalWidgetType.ProjectForms]: [
    CpWidgetVariantType.SingleFormWithImageAtTheLeft,
    CpWidgetVariantType.SingleFormWithImageAtTheRight,
    CpWidgetVariantType.SingleFormWithImageAtTheCenter,
    CpWidgetVariantType.MultiFormWithImageAtTheLeft,
    CpWidgetVariantType.MultiFormWithAllRoundBorder,
    CpWidgetVariantType.MultiFormWithBottomBorder,
  ],
};

export interface CustomerPortalWidget {
  widgetDefinition: WidgetMeta;
  widgetType: CustomerPortalWidgetType;
  widgetVariantType?: CpWidgetVariantType;
}

export enum CPWidgetRenderAs {
  Default = 'DEFAULT',
  Radio = 'RADIO',
  MultiLineText = 'MULTI_LINE_TEXT',
  ExtendableObjectsTabs = 'EXTENDABLE_OBJECTS_TABS',
  ExtendableObjectsList = 'EXTENDABLE_OBJECTS_LIST',
  SimpleSearchFilter = 'SIMPLE_SEARCH_FILTER',
  Image = 'IMAGE',
}

export enum CPWidgetRenderParams {
  HttpLink = 'HTTP_LINK',
  AllowSmartfills = 'ALLOW_SMART_FILLS',
}

export interface CustomerPortalAttachment {
  attachment: FileAttachment;
}

export type CustomerPortalField = {
  sourceType: ObjectType;
  fieldId: number;
  fieldName: string;
  fieldType: FieldTypes;
  fieldDataType: FieldDataType;
  customField: boolean;
  parentFieldName: string;
  objectType: ObjectType;
  fieldGroup: string;
  fieldColumnName: string;
  fieldLabel: string;
  private: false;
  isEditable: boolean;
  required?: boolean;
  sticky?: boolean;
  dynamic?: boolean;
  triggersDynamic?: boolean;
  placeholderValue: string;
  placeholderLabel: string;
  renderAs?: CPWidgetRenderAs;
  renderParams?: CPWidgetRenderParams;
  nestedFields: CustomerPortalField[];
  hint?: string;
  fieldValue?: unknown;
  placeholder?: string;
  actualFieldName?: string;
  metaData?: {
    optionsAvailable?: boolean;
    options: { label: string; valueStr: string }[];
    range?: {
      min: number;
      max: number;
    };
    rangeAvailable?: boolean;
    /**
     * This is just a FE property
     */
    allowRelativeDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowAbsDateOps?: boolean;
  };
};

export const LIVE_CP_PROJECT_ID = 'live-cp-project-id';
